import { AbstractControl, UntypedFormArray, ValidationErrors } from '@angular/forms';
import { APPLICATION_TYPES } from 'src/app/model/constants/application-types';
import { IApplication } from 'src/app/model/iapplication';
import { APPLICATION_NAMES, ATTACHMENT_NAMES } from './application-names';
import { VALIDATOR_ERRORS } from './validator-errors';

export class CustomValidators {

    private static readonly ssnRegex: RegExp = /^(19|20)?(\d{6}((-)?\d{4}|(-)?X\d{3})|(?!19|20)\d{10})$/;
    private static readonly dateOfBirthRegex: RegExp = /^(19|20)?(\d{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1])$/;

    public static nonForwardDate(control: AbstractControl): ValidationErrors | null {
        const value: string = control.value;
        if (!value) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.REQUIRED);
        }
        // Check that the value is a valid date.
        if (isNaN(Date.parse(value))) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.DATE_INCORECT);
        }
        // Check that the date is today or earlier.
        const date = Date.parse(value);
        const today = new Date().getTime();
        if (date > today) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.DATE_FORWARD);
        }
        return null;
    }

    public static ssn(control: AbstractControl): ValidationErrors | null {
        return CustomValidators.isValidSsn(control.value);
    }

    public static phone(control: AbstractControl): ValidationErrors | null {
        return CustomValidators.isValidPhone(control.value);
    }

    public static dateOfBirth(control: AbstractControl): ValidationErrors | null {
        const value: string = control.value;
        // Check that value is not empty and conforms to a valid format.
        if (!value || !CustomValidators.dateOfBirthRegex.test(value)) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.DATE_FORMAT);
        }
        return null;
    }

    public static requiresQualification(control: AbstractControl): ValidationErrors | null {
        const application: IApplication = control.value;
        if (application.type === APPLICATION_TYPES.SUPPLEMENT_SWEDISH
            && application.vocationalQualification.qualifications.length === 0
            && application.otherQualification.qualifications.length === 0
            && !application.specialEducation.specialEducationTeacher) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.QUALIFICATIONS);
        }
        return null;
    }

    private static isValidSsn(value: string): ValidationErrors | null {
        // Is 12-chars.
        if (!value || value.length !== 12) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.SSN_LENGTH);
        }
        // Is X-person.
        if (/^\d{8}X\d{3}$/.test(value)) {
            return null;
        }
        if (/^\d{8}x\d{3}$/.test(value)) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.SSN_CAPITAL_X);
        }
        // Is only numbers.
        if (!/^\d+$/.test(value)) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.SSN_CHARS);
        }
        // Has a valid checksum (luhn algorithm).
        const digits = value.split('').map((s) => Number(s));
        const constants = [0, 0, 2, 1, 2, 1, 2, 1, 2, 1, 2, 0];
        let sum = 0;
        for (let i = 0; i < digits.length; i++) {
            const part = digits[i] * constants[i];
            sum += part % 10 + Math.floor(part / 10);
        }
        const cSum = (10 - sum % 10) % 10;
        if (digits.pop() !== cSum) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.SSN_CHECK);
        }
        return null;
    }

    private static isValidPhone(value: string): ValidationErrors | null {
        if (value) {
            // Max 20-chars.
            if (value.length > 20) {
                return CustomValidators.buildError(VALIDATOR_ERRORS.PHONE_LENGTH);
            }
            // Is only numbers or + -.
            if (!/^[\d+-]+$/.test(value)) {
                return CustomValidators.buildError(VALIDATOR_ERRORS.PHONE_CHARS);
            }
        }
        return null;
    }

    public static supplement(group: AbstractControl): ValidationErrors | null {
        const attachments: UntypedFormArray = group.get([ATTACHMENT_NAMES.KEY, ATTACHMENT_NAMES.ATTACHMENTS.KEY].join('.')) as UntypedFormArray;
        const applicantText = group.get('applicantText');
        const attachmentsInvalid = attachments.length === 0;
        const applicantTextInvalid = !applicantText.value;

        if (attachmentsInvalid && applicantTextInvalid) {
            return CustomValidators.buildError(VALIDATOR_ERRORS.SUPPLEMENT);
        }
        return null;
    }

    private static buildError(input: { key: string }): { [key: string]: boolean } {
        const result = {};
        result[input.key] = true;
        return result;
    }
}
